import React from 'react'
import LoginFrame from '../shared/LoginFrame'
import AuthCollage from '../icons/AuthCollageIcon'
import { AuthMethodEnum, eventHandler } from '../../types.d'
import {
  useAppSelector,
  useAppDispatch,
  selectAuthConfig,
  selectAuthMethod,
} from '../../store/store'
import { enrollPasskey, completeAuthFlow } from '../../store'
import {
  InjectedFrameProps,
  withFrameProps,
} from '../../context/withFrameProps'
import { preventDefault } from '../../helpers'

type PureCreatePasskeyProps = {
  onContinue: eventHandler
  onSkip: eventHandler
  prompt: string
} & Exclude<InjectedFrameProps, 'hasPreviousStep'>

export const PureCreatePasskeyPrompt = ({
  onContinue,
  onSkip,
  prompt,
  ...props
}: PureCreatePasskeyProps) => (
  <LoginFrame title={null} recaptchaSiteKey={null} {...props}>
    <div className="space-y-4">
      <div className="flex items-center justify-center">
        <AuthCollage />
      </div>
      <p className="text-center font-medium text-lg">
        {prompt || 'With passkeys, signing in is easier and more secure.'}
      </p>

      <h1 className="font-medium">What are passkeys?</h1>
      <p className="!mt-1 !mb-3">
        Passkeys are encrypted digital keys you create using your fingerprint,
        face, or screen lock.
      </p>

      <h1 className="font-medium">Where are passkeys saved?</h1>
      <p className="!mt-1 !mb-0">
        Passkeys are saved in your password manager, so you can sign in on other
        devices.
      </p>
    </div>
    <button
      onClick={preventDefault(onContinue)}
      type="submit"
      tabIndex={1}
      className="w-full text-white bg-slate-600 hover:bg-slate-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
    >
      Create A Passkey
    </button>
    <p className="text-center text-sm font-light text-gray-500">
      <a
        href="#"
        onClick={preventDefault(onSkip)}
        tabIndex={2}
        className="font-medium text-primary-600 hover:underline"
      >
        Skip
      </a>
    </p>
  </LoginFrame>
)

const BoundCreatePasskeyPrompt = withFrameProps(PureCreatePasskeyPrompt, {
  hasPreviousStep: false,
})

export const CreatePasskeyPrompt = () => {
  const dispatch = useAppDispatch()
  const prompt = useAppSelector(selectAuthConfig).passkeyPrompt

  const handleContinue = async (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(enrollPasskey())
  }

  const handleSkip = (e: React.MouseEvent<HTMLAnchorElement>) => {
    dispatch(completeAuthFlow({ action: 'skip' }))
  }

  return (
    <BoundCreatePasskeyPrompt
      onContinue={handleContinue}
      onSkip={handleSkip}
      prompt={prompt}
    />
  )
}

export default CreatePasskeyPrompt
